import React, { useState, useEffect } from "react";
import { GetAllModules, GetValidLwFileNames } from "../../actions/adminQueries";
import { Button, Row, Col, Input, Table } from "reactstrap";
import CreateNewModule from "../../components/Modal/CreateNewModule";
import ConfirmDialog from "../../components/Modal/ConfirmDialog";
import { useQueryClient } from "@tanstack/react-query";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import { PacmanLoader } from "react-spinners";
import ModuleTypeIcon from "../../components/Module/ModuleTypeIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CourseAdminNav from "../../components/Navigation/CourseAdminNav";
import { FormattedMessage, useIntl } from "react-intl";
import axios from "../../services/api/api";
import { API_BASE_URL } from "../../constants/api.constants";
import { toast } from "react-toastify";
import { GetAvailableModuleAnswers } from "../../actions/teacherQueries";
import { formatDate } from "../../Utilities/dateUtils";
import { TOAST_CONFIG } from "../../Utilities/toastConfig";

function ModulesList() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [filterSearch, setFilterSearch] = useState("");
  const [modules, setModules] = useState([]);
  const [createNewModuleModal, setCreateNewModuleModal] = useState(false);
  const [editModule, setEditModule] = useState(null);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);
  const allModules = GetAllModules();
  const lwFileNames = GetValidLwFileNames();
  const availableModuleAnswers = GetAvailableModuleAnswers();

  useEffect(() => {
    if (allModules.data) {
      setModules(allModules.data);
    }
  }, [allModules.data]);

  useEffect(() => {
    if (allModules.data) {
      setModules(
        allModules.data.filter((it) =>
          it.name.toLowerCase().includes(filterSearch.toLowerCase()),
        ),
      );
    }
  }, [filterSearch, allModules.data]);

  const handleOpenAnswers = (moduleId) => {
    axios
      .get(`${API_BASE_URL}/Module/GetModuleAnswers?moduleId=${moduleId}`, {
        responseType: "blob",
      })
      .then((response) => {
        const fileURL = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = fileURL;
        link.download =
          availableModuleAnswers.data?.find((m) => m.moduleId === moduleId)
            ?.answersFile || `Facit-${moduleId}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(fileURL);
      })
      .catch(async (error) => {
        let responseObj = await error.response.data.text();
        if (responseObj.trim() === "FileNotFound") {
          toast.warn(
            <FormattedMessage id="Error_Code_Module_Answers_FileNotFound" />,
            TOAST_CONFIG,
          );
        } else {
          toast.warn(
            responseObj ? (
              responseObj
            ) : (
              <FormattedMessage id="GenericApiError" />
            ),
          );
        }
      });
  };

  const loadModules = () => {
    allModules.refetch();
  };

  const handleFilterSearch = (event) => {
    setFilterSearch(event.target.value);
  };

  if (allModules.isPending || availableModuleAnswers.isPending) {
    return (
      <div
        className="sweet-loading"
        style={{ position: "fixed", top: "50%", left: "50%" }}
      >
        <PacmanLoader color={"#20a8d8"} loading={true} />
      </div>
    );
  }
  const handleOpenAddNewModule = () => {
    setEditModule(null);
    setCreateNewModuleModal(true);
  };

  const handleOpenEditModule = (module) => {
    setEditModule(module);
    setCreateNewModuleModal(true);
  };

  const handleOpenDeleteDialog = (module) => {
    setModuleToDelete(module);
    setShowConfirmDeleteDialog(true);
  };

  const dismissConfirmDeleteDialog = () => {
    setShowConfirmDeleteDialog(false);
    setModuleToDelete(null);
  };

  const confirmDeleteModule = () => {
    axios
      .delete(`${API_BASE_URL}/Module/${moduleToDelete.id}`)
      .then(() => {
        toast.success(<FormattedMessage id="ModuleDeleted" />, TOAST_CONFIG);
        loadModules();
        queryClient.removeQueries({ queryKey: ["FetchModulesByCourseId"] });
        dismissConfirmDeleteDialog();
      })
      .catch((err) => {
        toast.warn(
          formatServiceError(intl, err.response?.data) ||
            intl.formatMessage({ id: "GenericApiError" }),
          TOAST_CONFIG,
        );
        dismissConfirmDeleteDialog();
      });
  };
  const handleToggleEditModal = () => {
    setCreateNewModuleModal(!createNewModuleModal);
    setEditModule(null);
    loadModules();
  };

  var mappedModulesComponent = modules.map((module, id) => {
    return (
      <tr key={id}>
        <td>
          <ModuleTypeIcon moduleType={module.moduleType} />
        </td>
        <td
          style={{
            maxWidth: "200px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <p
            title={module.name}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {" "}
            {module.name}
          </p>
          <span className="mt-2">
            {module.courses.map((course) => {
              return (
                <p className="small" title={course.name}>
                  <em>{course.name}</em>
                </p>
              );
            })}
          </span>
        </td>
        <td style={{ textAlign: "center" }}>
          {module.isFinalExam ? (
            <span>
              <FontAwesomeIcon icon="check" />
            </span>
          ) : (
            ""
          )}
        </td>
        <td style={{ textAlign: "center" }}>
          {module.maxScoreIsSetByModule ? (
            <span>
              <FontAwesomeIcon icon="check" />
            </span>
          ) : (
            ""
          )}
        </td>
        <td style={{ textAlign: "center" }}>
          {module.isFactModule ? (
            <span>
              <FontAwesomeIcon icon="check" />
            </span>
          ) : (
            ""
          )}
        </td>
        <td style={{ textAlign: "center" }} title={module.previewText || ""}>
          {module.isPreview ? (
            <span>
              <FontAwesomeIcon icon="check" />
            </span>
          ) : (
            ""
          )}
        </td>
        <td style={{ maxWidth: "120px" }}>
          <p
            title={module.qApiId}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              marginBottom: 0,
            }}
          >
            {module.qApiId}
          </p>
        </td>
        <td style={{ textAlign: "right" }}>
          <p>{module.maxScore}</p>
          <p>{module.approveScoreLimit}%</p>
        </td>
        <td style={{ textAlign: "right" }}>
          <p>{module.maxActivityPoints}</p>
        </td>
        <td
          style={{
            maxWidth: "50px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={module.code}
        >
          {module.code}
        </td>
        <td
          style={{
            maxWidth: "300px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <p> {module.description}</p>
        </td>
        <td style={{ textAlign: "right" }}>
          <p>{module.id}</p>
        </td>
        <td style={{ textAlign: "right" }}>
          <p>{module.moduleCoupleId}</p>
        </td>
        <td
          style={{
            maxWidth: "100px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={module.lwGuid}
        >
          {module.lwGuid}
        </td>
        <td
          style={{
            maxWidth: "200px",
          }}
        >
          <p
            title={module.url}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {" "}
            {module.url}
          </p>
        </td>
        <td>
          {availableModuleAnswers.data.some(
            (mod) => mod.moduleId === module.id,
          ) ? (
            <Button
              outline
              size="sm"
              color="primary"
              className="rounded-circle p-2 d-inline-flex align-items-center justify-content-center"
              onClick={() => handleOpenAnswers(module.id)}
              title={
                intl.formatMessage({ id: "UploadDate" }) +
                formatDate(
                  availableModuleAnswers.data.find(
                    (item) => item.moduleId === module.id,
                  ).answersFileDate,
                  "yyyy-MM-dd") +
                " " +
                intl.formatMessage({ id: "Filename" }) + ": " +
                availableModuleAnswers.data.find(
                  (item) => item.moduleId === module.id,
                ).answersFile
              }
            >
              <FontAwesomeIcon icon="check" />
            </Button>
          ) : (
            ""
          )}
        </td>
        <td style={{ whiteSpace: "nowrap" }}>
          <Button
            size="sm"
            outline
            color="primary"
            className="me-2"
            title={"Redigera modul:" + module.name}
            onClick={() => {
              handleOpenEditModule(module);
            }}
          >
            <FontAwesomeIcon icon="edit" title="Editera" />
          </Button>
          <span
            style={{ display: "inline-block" }}
            title={
              module.isPreview
                ? intl.formatMessage(
                    { id: "DeleteModuleTitle" },
                    { name: module.name },
                  )
                : intl.formatMessage({
                    id: "OnlyPreviewModulesCanBeDeletedTooltip",
                  })
            }
          >
            <Button
              size="sm"
              outline
              color={module.isPreview ? "danger" : "secondary"}
              disabled={!module.isPreview}
              onClick={
                module.isPreview
                  ? () => handleOpenDeleteDialog(module)
                  : undefined
              }
            >
              <FontAwesomeIcon icon="trash" />
            </Button>
          </span>
        </td>
      </tr>
    );
  });

  return (
    <div>
      <CourseAdminNav location="modules" />

      <div
        className="container-fluid admin-container"
        style={{ overflowX: "auto" }}
      >
        <Row>
          <Col className="col-12">
            <Row>
              <Col className="col-4">
                <Input
                  style={{ marginTop: "-10px", marginBottom: "10px" }}
                  className="search-input"
                  placeholder={intl.formatMessage({ id: "SearchModule" })}
                  onChange={handleFilterSearch.bind(this)}
                />
              </Col>
              <Col className="col-8">
                <Button
                  size="md"
                  outline
                  color="primary"
                  className="float-end"
                  style={{ marginTop: "-10px", marginBottom: "10px" }}
                  onClick={() => {
                    handleOpenAddNewModule();
                  }}
                >
                  <FontAwesomeIcon icon="plus" />
                  &nbsp;
                  <span className="">
                    <FormattedMessage id="NewModule" />
                  </span>
                </Button>
              </Col>
            </Row>
            <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
              <Table hover className="table table-striped align-middle">
                <thead className="sticky-header">
                  <tr>
                    <th>
                      <FormattedMessage id="Type" />
                    </th>
                    <th>
                      <FormattedMessage id="Name" />
                      <p className="small mb-0">
                        <FormattedMessage id="ConnectedTo" />
                      </p>
                    </th>
                    <th>
                      <FormattedMessage id="FinalExam" />
                    </th>
                    <th>
                      <FormattedMessage id="Gy25" />
                    </th>
                    <th>
                      <FormattedMessage id="FactModule" />
                    </th>
                    <th>Preview</th>
                    <th>
                      <FormattedMessage id="QApiId" />
                    </th>
                    <th style={{ textAlign: "right" }}>
                      <FormattedMessage id="Score" />
                      <p className="small mb-0">
                        <FormattedMessage id="Max" />,
                      </p>
                      <p className="small mb-0">
                        <FormattedMessage id="Views.Teacher.TeacherModule.CourseLimit" />
                      </p>
                    </th>
                    <th>
                      <FormattedMessage id="ActivityPoints" />
                    </th>
                    <th>
                      <FormattedMessage id="Code" />
                    </th>
                    <th>
                      <FormattedMessage id="Description" />
                    </th>
                    <th>Id</th>
                    <th>
                      <FormattedMessage id="CoupleId" />
                    </th>
                    <th>
                      <FormattedMessage id="LwGuid" />
                    </th>
                    <th>
                      <FormattedMessage id="Url" />
                    </th>
                    <th>
                      <FormattedMessage id="HasAnswers" />
                    </th>
                    <th>
                      <FormattedMessage id="Edit" />
                    </th>
                  </tr>
                </thead>
                <tbody>{mappedModulesComponent}</tbody>
              </Table>
            </div>
          </Col>
        </Row>
      </div>
      <CreateNewModule
        isOpen={createNewModuleModal}
        toggle={() => handleToggleEditModal()}
        reloadModules={loadModules}
        lwFileNames={lwFileNames}
        moduleToEdit={editModule}
        gy25Modules={allModules.data.filter(
          (m) => m.maxScoreIsSetByModule,
        )}
      />
      <ConfirmDialog
        isOpen={showConfirmDeleteDialog}
        title={<FormattedMessage id="RemovePreviewModule" />}
        message={
          intl.formatMessage({ id: "RemovePreviewModuleConfirm" }) +
          " " +
          (moduleToDelete?.name ?? "") +
          "?"
        }
        toggle={dismissConfirmDeleteDialog}
        onProceed={confirmDeleteModule}
        danger={true}
      />
    </div>
  );
}

export default ModulesList;
